import { Color3, type Mesh, MeshBuilder, StandardMaterial, Vector3 } from "@babylonjs/core";
import { BARRIERS, Btn, DOCTOR_VARS, HeroId, MoveKind, Slot, fxk, hv, pk, zk } from "@rivalwatch/shared";
import * as loops from "../../audio/cues/loops.ts";
import { bell, choir, trim } from "../../audio/synth.ts";
import type { ClientHero } from "../types.ts";
import { doctorFire, doctorGateway, doctorMaelstrom, portalOpen, wardBreak, wardDown, wardUp } from "../audio-cues.ts";

const PK = { Dagger: pk(HeroId.DoctorEstranged, 0) } as const;
const ZK = { Portal: zk(HeroId.DoctorEstranged, 0) } as const;
const FX = { Maelstrom: fxk(HeroId.DoctorEstranged, 0), SoulSever: fxk(HeroId.DoctorEstranged, 1) } as const;
const doctorMaelstromHot = trim(5, doctorMaelstrom);

const theme = {
  bpm: 92,
  octave: 0,
  voice: choir,
  double: (o, f, d, v) => void bell(o, f * 2, d, v * 0.55),
  notes: [
    [0, 1],
    [6, 1],
    [7, 1],
    [16, 2],
  ],
} satisfies ClientHero["audio"]["theme"];

type Gear = { amulet: Mesh; sigilL: Mesh; sigilR: Mesh; cape: Mesh; collar: Mesh };
const mat = (scene: Parameters<NonNullable<ClientHero["projectiles"]>[number]>[0], hex: string, alpha = 1) => {
  const m = new StandardMaterial("doctor-mat", scene);
  m.diffuseColor = Color3.FromHexString(hex);
  m.emissiveColor = Color3.FromHexString(hex).scale(0.4);
  m.alpha = alpha;
  return m;
};

const hero: ClientHero<Gear> = {
  id: HeroId.DoctorEstranged,
  bulk: 1.2,
  shape: { head: 1, torso: 1.08, arms: 1.02, legs: 1.02 },
  gear(g) {
    const cloak = g.toon("#8e2035");
    const trim = g.toon("#d8b15a");
    const dark = g.toon("#101527");
    const eye = g.glow("#58d7ff", 0.9);
    const cape = g.add(MeshBuilder.CreateBox("estranged-cape", { width: 1.12, height: 1.25, depth: 0.055 }, g.scene), g.rig.torso, cloak);
    cape.position.set(0, -0.28, 0.48);
    cape.rotation.x = 0.08;
    const collar = g.add(MeshBuilder.CreateBox("estranged-collar", { width: 1.08, height: 0.22, depth: 0.12 }, g.scene), g.rig.torso, cloak);
    collar.position.set(0, 0.28, 0.43);
    const collarTrim = g.add(MeshBuilder.CreateBox("estranged-collar-trim", { width: 0.9, height: 0.08, depth: 0.14 }, g.scene), g.rig.torso, trim);
    collarTrim.position.set(0, 0.37, 0.39);
    for (const side of [-1, 1]) {
      const pauldron = g.add(MeshBuilder.CreateBox("estranged-pauldron", { width: 0.38, height: 0.18, depth: 0.46 }, g.scene), side < 0 ? g.rig.armL : g.rig.armR, dark);
      pauldron.position.set(side * 0.1, 0.03, 0.02);
      pauldron.rotation.z = -side * 0.2;
    }
    const amulet = g.add(MeshBuilder.CreateSphere("eye-amulet", { diameter: 0.2, segments: 12 }, g.scene), g.rig.torso, eye);
    amulet.position.set(0, 0.07, -0.43);
    const iris = g.add(MeshBuilder.CreateTorus("eye-amulet-ring", { diameter: 0.28, thickness: 0.025, tessellation: 24 }, g.scene), g.rig.torso, trim);
    iris.position.set(0, 0.07, -0.44);
    const sigilL = g.add(MeshBuilder.CreateTorus("sigil-left", { diameter: 0.48, thickness: 0.025, tessellation: 32 }, g.scene), g.rig.handL, eye);
    const sigilR = g.add(MeshBuilder.CreateTorus("sigil-right", { diameter: 0.48, thickness: 0.025, tessellation: 32 }, g.scene), g.rig.handR, eye);
    for (const sigil of [sigilL, sigilR]) {
      sigil.rotation.x = Math.PI / 2;
      sigil.position.z = -0.38;
    }
    return { amulet, sigilL, sigilR, cape, collar };
  },
  animate(gear, f) {
    const levitating = f.moveKind === MoveKind.Flight;
    const casting = f.action === 4 || f.action === 5 || levitating;
    gear.sigilL.setEnabled(casting || f.action === 1);
    gear.sigilR.setEnabled(casting || f.action === 1);
    gear.sigilL.rotation.z += f.dt * 3.5;
    gear.sigilR.rotation.z -= f.dt * 4.2;
    gear.amulet.scaling.setAll(1 + Math.min(0.4, f.v[DOCTOR_VARS.darkMagic] / 250) + (casting ? 0.12 * Math.sin(f.t * 9) : 0));
    gear.cape.rotation.x = 0.08 + (levitating ? 0.18 + Math.sin(f.t * 3) * 0.04 : 0);
    gear.collar.rotation.x = levitating ? -0.08 : 0;
    if (levitating) return "holding-both";
    if (f.action === 1) return "holding-both-shoot";
    if (f.action === 4 || f.action === 5) return "interact-right";
    return undefined;
  },
  hud(c, eid) {
    return [
      { label: "Ward", value: hv(c, eid, DOCTOR_VARS.barrierHp) / BARRIERS[HeroId.DoctorEstranged]!.maxHp },
      { label: "Dark magic", value: hv(c, eid, DOCTOR_VARS.darkMagic) / 100 },
      { label: "Cloak", value: hv(c, eid, DOCTOR_VARS.levitateTicks) / 360 },
    ];
  },
  lifting(c, eid, buttons, grounded) {
    return !grounded && hv(c, eid, DOCTOR_VARS.levitating) > 0 && !!(buttons & Btn.Jump);
  },
  projectiles: {
    [PK.Dagger]: (scene, frame) => {
      const blade = MeshBuilder.CreateCylinder("denial-dagger", { diameterTop: 0.02, diameterBottom: 0.14, height: 0.72, tessellation: 5 }, scene);
      blade.material = mat(scene, frame.rel === "foe" ? "#ff9a3c" : "#58d7ff", 0.95);
      return {
        gear: { blade },
        update({ blade }, f) {
          blade.position.set(f.pos.x, f.pos.y, f.pos.z);
          blade.lookAt(new Vector3(f.pos.x + f.dir.x, f.pos.y + f.dir.y, f.pos.z + f.dir.z));
          blade.rotation.x += Math.PI / 2;
          blade.rotation.z += f.age * 10;
        },
        dispose: () => blade.dispose(),
      };
    },
  },
  zones: {
    [ZK.Portal]: (scene, frame) => {
      const outer = MeshBuilder.CreateTorus("portal-outer", { diameter: frame.radius * 2.1, thickness: 0.055, tessellation: 48 }, scene);
      const inner = MeshBuilder.CreateTorus("portal-inner", { diameter: frame.radius * 1.35, thickness: 0.025, tessellation: 40 }, scene);
      outer.material = mat(scene, "#ff9a3c", 0.82);
      inner.material = mat(scene, "#58d7ff", 0.68);
      return {
        gear: { outer, inner },
        update({ outer, inner }, f) {
          for (const ring of [outer, inner]) {
            ring.position.set(f.pos.x, f.pos.y + 1.35, f.pos.z);
            ring.rotation.x = Math.PI / 2;
            ring.rotation.z += f.dt * (ring === outer ? 1.2 : -2.2);
            ring.scaling.setAll(1 + Math.sin(f.age * 4) * 0.035);
          }
        },
        dispose() {
          outer.dispose();
          inner.dispose();
        },
      };
    },
  },
  fx: {
    [FX.Maelstrom]: {
      explosion(c, pos, radius) {
        const ring = MeshBuilder.CreateTorus("maelstrom-ring", { diameter: radius * 2, thickness: 0.12, tessellation: 48 }, c.scene);
        ring.material = c.glow("#8a6cff", 0.55);
        ring.position.set(pos.x, pos.y + 0.08, pos.z);
        c.timed(ring, 0.55, (t, m) => {
          m.scaling.setAll(0.25 + t * 0.9);
          m.rotation.y += 0.18;
        });
      },
    },
    [FX.SoulSever]: {
      explosion(c, pos, radius) {
        const eye = MeshBuilder.CreateSphere("soul-eye", { diameter: radius * 0.5, segments: 16 }, c.scene);
        eye.material = c.glow("#58d7ff", 0.5);
        eye.position.set(pos.x, pos.y + 1.6, pos.z);
        c.timed(eye, 0.8, (t, m) => {
          m.scaling.set(1 + t * 2.8, Math.max(0.08, 0.45 - t * 0.35), 1 + t * 2.8);
        });
      },
    },
  },
  icons: {
    [Slot.Primary]: "M5 7l6 5-6 5M13 7l6 5-6 5",
    [Slot.Secondary]: "M4 18V8l8-4 8 4v10M8 12h8",
    [Slot.Ability1]: "M6 16c3-8 9-8 12 0M8 18c3-3 5-3 8 0",
    [Slot.Ability2]: "M12 4v16M4 12h16M6 6l12 12M18 6 6 18",
    [Slot.Ability3]: "M7 12a5 7 0 1 0 10 0 5 7 0 1 0-10 0M3 12h4M17 12h4",
    [Slot.Ultimate]: "M12 5c4 0 7 3 9 7-2 4-5 7-9 7s-7-3-9-7c2-4 5-7 9-7zM12 9v6",
    passive: "M12 3l3 7 7 2-7 2-3 7-3-7-7-2 7-2z",
  },
  audio: {
    weight: "heavy",
    fire: () => ({ key: "fire:doctor-estranged", cue: doctorFire }),
    ability: {
      [Slot.Ability1]: doctorGateway,
      [Slot.Ability3]: doctorGateway,
      [Slot.Ability2]: doctorMaelstromHot,
    },
    theme,
    themeTrim: { heroic: -3.5, hostile: -6.4, tease: 3.5 },
    loops: ({ v, alive }) => [{ key: "ward", build: loops.ward, gain: 0.8, value: v[1] / BARRIERS[HeroId.DoctorEstranged]!.maxHp }].filter(() => alive && v[0] > 0),
    transitions(prev, next) {
      const was = prev[0] > 0;
      const now = next[0] > 0;
      if (now && !was) return [wardUp];
      if (!now && was) return [prev[1] / BARRIERS[HeroId.DoctorEstranged]!.maxHp < 0.03 ? wardBreak : wardDown];
      return [];
    },
    selfLift: ({ alive }) => (alive ? loops.levitate : undefined),
    flight: { [PK.Dagger]: loops.flight("orb") },
    zoneLoops: {
      [ZK.Portal]: { build: loops.portal, gain: 0.7, range: 32 },
    },
    zoneStarts: {
      [ZK.Portal]: portalOpen,
    },
  },
};

export default hero;
